// client/src/services/notificationService.js
import axios from 'axios';
import { getSocket } from './socketService';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/notifications`;

// Create axios instance with auth interceptor
const notificationApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
notificationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add tenant header
    const tenant = localStorage.getItem('tenant');
    if (tenant) {
      try {
        const tenantData = JSON.parse(tenant);
        if (tenantData && tenantData._id) {
          config.headers['x-tenant-id'] = tenantData._id;
        }
      } catch (error) {
        console.error('Error parsing tenant data:', error);
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

const notificationService = {
  // Get all notifications with pagination
  getNotifications: async (page = 1, limit = 20) => {
    try {
      const response = await notificationApi.get('/', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  // Get notification count (unread + total)
  getNotificationCount: async () => {
    try {
      const response = await notificationApi.get('/count');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching notification count:', error);
      throw error;
    }
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    try {
      const response = await notificationApi.patch(`/${notificationId}/read`);
      return response.data.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    try {
      const response = await notificationApi.patch('/read-all');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Create message notification
  createMessageNotification: async (recipientId, conversationId, messagePreview) => {
    try {
      const response = await notificationApi.post('/message', {
        recipientId,
        conversationId,
        messagePreview
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating message notification:', error);
      throw error;
    }
  },

  // Create appointment notification
  createAppointmentNotification: async (type, appointmentData) => {
    try {
      const response = await notificationApi.post('/appointment', {
        type,
        appointmentId: appointmentData.appointmentId,
        appointmentDate: appointmentData.appointmentDate,
        patientId: appointmentData.patientId
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating appointment notification:', error);
      throw error;
    }
  },

  // Create mood check-in notification
  createMoodNotification: async (patientId, moodData, doctorId) => {
    try {
      const response = await notificationApi.post('/mood', {
        patientId,
        moodData,
        doctorId
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating mood notification:', error);
      throw error;
    }
  },

  // Create system notification
  createSystemNotification: async (recipientId, title, message, data = {}) => {
    try {
      const response = await notificationApi.post('/system', {
        recipientId,
        title,
        message,
        data
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating system notification:', error);
      throw error;
    }
  },

  registerFCMToken: async (token) => {
    try {
      const response = await notificationApi.post('/fcm-token', { 
        token,
        platform: 'web',
        // Optional: Add any additional metadata
        deviceType: 'browser'
      });
      console.log('FCM Token registered successfully');
      return response.data;
    } catch (error) {
      console.error('Error registering FCM token:', error);
      throw error;
    }
  },

  // Socket event listeners
 // client/src/services/notificationService.js - Add specific event name in subscribeToNotifications

subscribeToNotifications: (callbacks) => {
  const socket = getSocket();
  if (!socket) {
    console.warn('Socket not available for notification subscription');
    return () => {};
  }

  const unsubscribers = [];

  // Update these event names to match server events
  if (callbacks.onNewNotification) {
    socket.on('newNotification', callbacks.onNewNotification);
    unsubscribers.push(() => socket.off('newNotification', callbacks.onNewNotification));
  }

  if (callbacks.onCountUpdate) {
    socket.on('notificationCountUpdate', callbacks.onCountUpdate);
    unsubscribers.push(() => socket.off('notificationCountUpdate', callbacks.onCountUpdate));
  }

  // Message notifications
  if (callbacks.onNewMessage) {
    socket.on('newMessage', callbacks.onNewMessage);
    unsubscribers.push(() => socket.off('newMessage', callbacks.onNewMessage));
  }

  // Appointment notifications
  if (callbacks.onAppointmentUpdate) {
    socket.on('appointmentNotification', callbacks.onAppointmentUpdate);
    unsubscribers.push(() => socket.off('appointmentNotification', callbacks.onAppointmentUpdate));
  }

  // NEW: Specific handler for appointment requests
  if (callbacks.onAppointmentRequest) {
    socket.on('appointmentRequest', callbacks.onAppointmentRequest);
    unsubscribers.push(() => socket.off('appointmentRequest', callbacks.onAppointmentRequest));
  }

  // Mood check-in notifications
  if (callbacks.onMoodCheckIn) {
    socket.on('moodCheckIn', callbacks.onMoodCheckIn);
    unsubscribers.push(() => socket.off('moodCheckIn', callbacks.onMoodCheckIn));
  }

  // System notifications
  if (callbacks.onSystemNotification) {
    socket.on('systemNotification', callbacks.onSystemNotification);
    unsubscribers.push(() => socket.off('systemNotification', callbacks.onSystemNotification));
  }

  // Payment proof notifications
if (callbacks.onPaymentProofUploaded) {
  socket.on('paymentProofUploaded', callbacks.onPaymentProofUploaded);
  unsubscribers.push(() => socket.off('paymentProofUploaded', callbacks.onPaymentProofUploaded));
}

  // Return unsubscribe function
  return () => {
    unsubscribers.forEach(unsubscribe => unsubscribe());
  };
},

  // Helper to check if a notification is unread
  isUnread: (notification) => !notification.read,

  // Helper to format notification time
  formatNotificationTime: (createdAt) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.round(diffMs / 1000);
    const diffMins = Math.round(diffSecs / 60);
    const diffHours = Math.round(diffMins / 60);
    const diffDays = Math.round(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  },

  // Helper to get notification icon based on type
  getNotificationIcon: (type) => {
    const icons = {
      message: '💬',
      appointment: '📅',
      mood: '😊',
      system: '🔔',
      call: '📞',
      other: '📌'
    };
    return icons[type] || '🔔';
  },

  // Helper to get notification color based on type
  getNotificationColor: (type) => {
    const colors = {
      message: '#2196F3',
      appointment: '#4CAF50',
      mood: '#FF9800',
      system: '#9C27B0',
      call: '#F44336',
      other: '#607D8B'
    };
    return colors[type] || '#757575';
  }
};

export default notificationService;